// Mappings — Crosswalks between coding systems (live API)
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { MappingItem, MappingSet } from "@/lib/types";

type RowStatus = "verified" | "auto" | "pending";

function statusOf(i: MappingItem): RowStatus {
  if (i.verified) return "verified";
  if (i.targetId) return "auto";
  return "pending";
}

const STATUS_TONE: Record<RowStatus, { bg: string; fg: string; label: string }> = {
  verified: { bg: "var(--md-secondary-container)", fg: "var(--md-on-secondary-container)", label: "Verified" },
  auto: { bg: "var(--md-tertiary-container)", fg: "var(--md-on-tertiary-container)", label: "Auto" },
  pending: { bg: "var(--md-sc-high)", fg: "var(--md-on-surface-var)", label: "Pending" },
};

export function Mappings() {
  const [sets, setSets] = useState<MappingSet[]>([]);
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const [items, setItems] = useState<MappingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unmapped" | "low">("all");
  const [editing, setEditing] = useState<MappingItem | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const active = sets.find(s => s.id === activeSetId) ?? sets[0] ?? null;

  async function loadSets() {
    try {
      const s = await api.mappings.sets();
      setSets(s);
      if (!activeSetId && s[0]) setActiveSetId(s[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load mapping sets");
    }
  }

  async function loadItems() {
    if (!active) return;
    setLoading(true); setError(null);
    try {
      const p = await api.mappings.items(active.id, { pageSize: 200 });
      setItems(p.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load items");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSets(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (active) loadItems(); /* eslint-disable-next-line */ }, [active?.id]);

  const filtered = useMemo(() => items.filter(r => {
    if (filter === "unmapped") return !r.targetId;
    if (filter === "low") return r.confidence > 0 && r.confidence < 0.8;
    return true;
  }), [items, filter]);

  const autoMapAll = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await api.mappings.autoMap(active.id);
      toast(r.mapped ? `Auto-mapped ${r.mapped} entries` : "Nothing to auto-map", r.mapped ? "ok" : "info");
      await loadItems();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Auto-map failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!active || !editing) return;
    try {
      await api.mappings.updateItem(active.id, editing.id, {
        targetId: editing.targetId,
        confidence: editing.confidence,
        notes: editing.notes,
      });
      setEditing(null);
      toast(`Saved mapping ${editing.sourceId} → ${editing.targetId || "?"}`, "ok");
      await loadItems();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Save failed", "error");
    }
  };

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <header style={{ ...panel, padding: 20 }}>
        <div style={eyebrow}>CROSSWALK</div>
        <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400 }}>Mappings</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", letterSpacing: "0.08em" }}>MAPPING SET</span>
            <select value={active?.id ?? ""} onChange={e => setActiveSetId(e.target.value)} style={{ ...input, padding: "8px 12px" }}>
              {sets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          {active && (
            <span style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
              {active.sourceDomainId} → {active.targetDomainId} · {active.itemCount} items
            </span>
          )}
          <span style={{ flex: 1 }} />
          <button onClick={autoMapAll} disabled={busy || !active} style={filledBtn}>{busy ? "Working…" : "Auto-map unmapped"}</button>
        </div>
      </header>

      {error && <div style={{ ...panel, padding: 16, color: "var(--md-error)" }}>{error}</div>}

      <div style={{ ...panel, padding: 0 }}>
        <div style={{ display: "flex", gap: 8, padding: 12, borderBottom: "1px solid var(--md-outline-var)", alignItems: "center" }}>
          {(["all", "unmapped", "low"] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ ...pillBtn, ...(filter === f ? pillBtnActive : {}) }}>
              {f === "all" ? "All" : f === "unmapped" ? "Unmapped only" : "Low confidence (<80%)"}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <span style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>{filtered.length} of {items.length} mappings</span>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>Loading mappings…</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ font: "var(--t-label-md)", color: "var(--md-on-surface-var)" }}>
                <th style={th}>Source</th>
                <th style={th}>Target</th>
                <th style={th}>Confidence</th>
                <th style={th}>Status</th>
                <th style={th}>Verified by</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const s = statusOf(r);
                const tone = STATUS_TONE[s];
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                    <td style={{ ...td, font: "var(--t-mono-sm)", fontWeight: 600 }}>{r.sourceId}</td>
                    <td style={{ ...td, font: "var(--t-mono-sm)", fontWeight: 600, color: r.targetId ? "var(--md-on-surface)" : "var(--md-error)" }}>{r.targetId || "—"}</td>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 80, height: 6, background: "var(--md-sc-high)", borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${r.confidence * 100}%`, height: "100%", background: r.confidence > 0.9 ? "var(--forest-500)" : r.confidence > 0.75 ? "var(--ochre-400)" : "var(--rust-500)" }} />
                        </div>
                        <span style={{ font: "var(--t-mono-sm)" }} className="tabular">{Math.round(r.confidence * 100)}%</span>
                      </div>
                    </td>
                    <td style={td}>
                      <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: "var(--r-pill)", background: tone.bg, color: tone.fg, font: "var(--t-label-sm)" }}>
                        {tone.label}
                      </span>
                    </td>
                    <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{r.verifiedBy ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <button onClick={() => setEditing({ ...r })} style={pillBtn} aria-label="Edit mapping">✎</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div style={scrim} onClick={() => setEditing(null)} role="dialog" aria-modal="true">
          <aside style={drawer} onClick={e => e.stopPropagation()}>
            <div style={eyebrow}>EDIT MAPPING</div>
            <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400, marginBottom: 12 }}>
              {editing.sourceId} → {editing.targetId || "?"}
            </div>
            <Field label="Source"><input style={input} value={editing.sourceId} readOnly /></Field>
            <Field label="Target"><input style={input} value={editing.targetId ?? ""} onChange={e => setEditing({ ...editing, targetId: e.target.value })} /></Field>
            <Field label="Confidence (0-1)">
              <input style={input} type="number" step="0.01" min="0" max="1"
                value={editing.confidence}
                onChange={e => setEditing({ ...editing, confidence: Math.min(1, Math.max(0, Number(e.target.value))) })} />
            </Field>
            <Field label="Notes"><textarea style={{ ...input, minHeight: 80 }} value={editing.notes ?? ""} onChange={e => setEditing({ ...editing, notes: e.target.value })} /></Field>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setEditing(null)} style={pillBtn}>Cancel</button>
              <button onClick={saveEdit} style={filledBtn}>Save & verify</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 4, marginBottom: 10 }}>
      <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>{label}</span>
      {children}
    </label>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)", padding: 16 };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const input: CSSProperties = { width: "100%", padding: "8px 12px", borderRadius: "var(--r-md)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)" };
const pillBtn: CSSProperties = { padding: "6px 12px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };
const pillBtnActive: CSSProperties = { background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", borderColor: "transparent" };
const filledBtn: CSSProperties = { padding: "8px 16px", borderRadius: "var(--r-pill)", border: "none", background: "var(--md-primary)", color: "var(--md-on-primary)", cursor: "pointer", font: "var(--t-label-lg)" };
const th: CSSProperties = { textAlign: "left", padding: "10px 14px", font: "var(--t-label-md)" };
const td: CSSProperties = { padding: "10px 14px", font: "var(--t-body-md)" };
const scrim: CSSProperties = { position: "fixed", inset: 0, background: "var(--md-scrim)", display: "flex", justifyContent: "flex-end", zIndex: 100 };
const drawer: CSSProperties = { width: 420, height: "100%", background: "var(--md-sc-lowest)", padding: 24, overflowY: "auto", boxShadow: "var(--e-4)" };

export default Mappings;
